import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useEscapeToClose } from '../hooks/use-escape-to-close.js';

export interface DialogProps {
  onClose: () => void;
  children: ReactNode;
  /** Extra class names on the panel (in addition to `dialog`). */
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

/**
 * Standard modal shell for the web app.
 *
 * Handles the close button, backdrop dismiss, Escape, body scroll lock, and dialog ARIA.
 * Prefer this over hand-rolling `dialog-backdrop` markup so every modal
 * behaves the same way.
 */
export function Dialog({
  onClose,
  children,
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: DialogProps) {
  useEscapeToClose(onClose);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return (
    <div className="dialog-backdrop" onClick={onClose} role="presentation">
      <div
        className={className ? `dialog ${className}` : 'dialog'}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        onClick={(event) => event.stopPropagation()}
      >
        {children}
        {/* After the content so Tab reaches the dialog's own fields first. */}
        <button type="button" className="dialog-close" aria-label="Close" onClick={onClose}>
          <svg width={20} height={20} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
              d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
